/** The basket, and what it is worth at the minute being replayed.
 *
 * The header numbers are labelled by BASIS rather than assumed. Rupees
 * need the contract's lot size, which the archive only holds from
 * 2026-08-28; before that the endpoint reports premium points and this
 * says so, instead of printing a rupee figure that is quietly wrong by a
 * factor of the lot.
 */

import { useState, type ReactElement } from "react";
import { cn } from "@/lib/cn";
import type { SimPayoff } from "@/lib/simulatorApi";
import { breakevenPct, costWord, money, pnlTone, signed } from "./positionsFormat";
import { CommitNumberInput } from "./CommitNumberInput";
import { LegsTable } from "./PositionsTable";

interface Props {
  payoff: SimPayoff;
  at: string;
  onClose: (index: number) => void;
  onReopen: (index: number) => void;
  onMark: (index: number, price: number) => void;
  onExit: (index: number, price: number) => void;
  onResetMark: (index: number) => void;
  onCloseAll: () => void;
  multiplier: number;
  maxMultiplier: number;
  onMultiplier: (n: number) => void;
  onRemove: (index: number) => void;
  onLots: (index: number, lots: number) => void;
  onEntry: (index: number, entry: number) => void;
  onClear: () => void;
}

/** Where the lot size behind these rupees came from.
 *
 * Three states, and only the last is a warning:
 *   archived — the session carried its own, captured live at 15:40.
 *   assumed  — the session predates lot-size capture, so it is priced at
 *              the value the operator confirmed applied across the whole
 *              archived stretch. That is a CONFIRMED number, not a guess,
 *              so it reads as plain information rather than a caution —
 *              a warning nobody needs to act on only trains people to
 *              ignore the ones they do.
 *   none     — nobody has confirmed a lot size for this index, so the
 *              numbers are premium points and that DOES need saying.
 *
 * Either way the lot size is named, because "how big is this" is the
 * question a rupee figure immediately raises.
 */
function LotBasis({ payoff }: Readonly<{ payoff: SimPayoff }>): ReactElement | null {
  if (payoff.basis === "points") {
    return (
      <span className="pill bg-warning-bg text-warning">
        points only — no lot size confirmed for this index
      </span>
    );
  }
  if (payoff.lot_size === null) return null;
  return (
    <span className="text-xs text-ink-secondary">
      Lot size <span className="font-mono tabular-nums text-ink">{payoff.lot_size}</span>
    </span>
  );
}

/** Net delta / theta / vega of the legs live at this minute.
 *
 * OFF by default (operator, 2026-09-18: "with option by default disable") —
 * the summary row is already four numbers wide, and greeks are a question you
 * ask deliberately. A missing greek shows a dash: the archive solved none
 * before 2026, and a 0 there would read as a flat position.
 */
function greek(value: number | null): string {
  if (value === null) return "—";
  return value.toFixed(2);
}

function NetGreeks({ payoff }: Readonly<{ payoff: SimPayoff }>): ReactElement {
  const cells: [string, number | null][] = [
    ["Delta", payoff.net_delta],
    ["Theta", payoff.net_theta],
    ["Vega", payoff.net_vega],
  ];
  return (
    <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm" data-testid="net-greeks">
      {cells.map(([label, value]) => (
        <div key={label}>
          <span className="text-ink-secondary">{label} </span>
          <span className="font-mono tabular-nums text-ink">{greek(value)}</span>
        </div>
      ))}
    </div>
  );
}

/** One labelled number in the summary row. */
function Stat({
  label,
  children,
}: Readonly<{ label: string; children: ReactElement | string }>): ReactElement {
  return (
    <div>
      <span className="text-ink-secondary">{label} </span>
      <span className="font-mono tabular-nums text-ink">{children}</span>
    </div>
  );
}

/** What was paid or taken in to open the basket, and what carrying it blocks.
 *
 * Shown unsigned with the word beside it — see `costWord`. Margin is a dash on
 * an unhedged short, where SPAN is not knowable from an archived session
 * (`SimPayoffResponse.margin`); the word "margin" stays, so a dash reads as
 * "not available" rather than as "nothing blocked".
 */
function Money({ payoff }: Readonly<{ payoff: SimPayoff }>): ReactElement {
  const { basis } = payoff;
  const word = costWord(payoff.net_entry_cost);
  return (
    <>
      <Stat label="Cost">
        <>
          {money(Math.abs(payoff.net_entry_cost), basis)}
          {word && <span className="ml-1 text-xs text-ink-secondary">{word}</span>}
        </>
      </Stat>
      <Stat label="Margin">{money(payoff.margin, basis)}</Stat>
    </>
  );
}

/** Each breakeven, with how far the index must travel to reach it. */
function Breakevens({ payoff }: Readonly<{ payoff: SimPayoff }>): ReactElement {
  if (payoff.breakevens.length === 0) return <Stat label="Breakeven">—</Stat>;
  return (
    <Stat label={payoff.breakevens.length === 1 ? "Breakeven" : "Breakevens"}>
      <>
        {payoff.breakevens.map((b, i) => {
          const pct = breakevenPct(b, payoff.spot);
          return (
            <span key={b}>
              {i > 0 && " / "}
              {b.toFixed(0)}
              {pct !== null && <span className="ml-1 text-xs text-ink-secondary">{pct}</span>}
            </span>
          );
        })}
      </>
    </Stat>
  );
}

function Summary({ payoff }: Readonly<{ payoff: SimPayoff }>): ReactElement {
  const { basis } = payoff;
  const mtm = payoff.last_mtm;
  return (
    <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
      <div>
        <span className="text-ink-secondary">P&amp;L </span>
        <span className={cn("font-mono tabular-nums", pnlTone(mtm))}>
          {signed(mtm)}
          {money(mtm, basis)}
        </span>
      </div>
      <Money payoff={payoff} />
      <Stat label="Max profit">
        {payoff.max_profit_unbounded ? "Unlimited" : money(payoff.max_profit, basis)}
      </Stat>
      <Stat label="Max loss">
        {payoff.max_loss_unbounded ? "Unlimited" : money(payoff.max_loss, basis)}
      </Stat>
      <Breakevens payoff={payoff} />
      <LotBasis payoff={payoff} />
      <GreeksToggle payoff={payoff} />
    </div>
  );
}

function GreeksToggle({ payoff }: Readonly<{ payoff: SimPayoff }>): ReactElement {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        className="btn btn-ghost px-2 py-0.5 text-xs"
        aria-pressed={open}
        onClick={() => {
          setOpen(!open);
        }}
      >
        {open ? "Hide greeks" : "Greeks"}
      </button>
      {open && <NetGreeks payoff={payoff} />}
    </>
  );
}

function Nudge({
  label,
  glyph,
  disabled,
  onClick,
}: Readonly<{
  label: string;
  glyph: string;
  disabled: boolean;
  onClick: () => void;
}>): ReactElement {
  return (
    <button
      type="button"
      className="btn btn-ghost px-1.5 py-0 text-xs leading-none"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
    >
      {glyph}
    </button>
  );
}

/** Size the WHOLE basket at once.
 *
 * A four-legged condor is four Lots fields; taking it from one clip to
 * three should not be twelve clicks, and doing it by hand is where a leg
 * gets missed and the structure quietly stops being a condor.
 *
 * ⚠️ It does NOT edit the legs. The table keeps showing the basket as
 * built, so a x3 ratio spread still reads as the 1:2 it is — and going
 * back to x1 is exact, where dividing the lots back down would round the
 * ratio and silently change the trade.
 *
 * ⚠️ The ceiling is per-basket. The biggest leg hits the API's 100-lot cap
 * first, so a 2-lot ratio leg stops the whole basket at x50 where a
 * straddle of 1-lot legs reaches x100. Showing that limit beats letting
 * someone set a number that fails the pricing call.
 */
function Multiplier({
  multiplier,
  maxMultiplier,
  onMultiplier,
}: Readonly<{
  multiplier: number;
  maxMultiplier: number;
  onMultiplier: (n: number) => void;
}>): ReactElement {
  return (
    <span className="flex items-center gap-1">
      <span className="text-xs text-ink-secondary">Multiplier</span>
      <Nudge
        label="One fewer of the basket"
        glyph="−"
        disabled={multiplier <= 1}
        onClick={() => {
          onMultiplier(multiplier - 1);
        }}
      />
      <CommitNumberInput
        label="Multiplier"
        className="input h-6 w-12 px-1 py-0 text-center font-mono text-xs tabular-nums"
        min={1}
        max={maxMultiplier}
        value={multiplier}
        onCommit={onMultiplier}
      />
      <Nudge
        label="One more of the basket"
        glyph="+"
        disabled={multiplier >= maxMultiplier}
        onClick={() => {
          onMultiplier(multiplier + 1);
        }}
      />
      {multiplier > 1 && (
        <span className="text-[11px] text-ink-muted">every leg &times;{multiplier}</span>
      )}
    </span>
  );
}

export function PositionsPanel(props: Readonly<Props>): ReactElement {
  const { payoff, multiplier, maxMultiplier, onMultiplier, onCloseAll, onClear } = props;
  return (
    <div className="flex flex-col gap-3">
      <Summary payoff={payoff} />
      <LegsTable {...props} />
      <div className="flex flex-wrap items-center gap-3">
        <Multiplier
          multiplier={multiplier}
          maxMultiplier={maxMultiplier}
          onMultiplier={onMultiplier}
        />
        {payoff.legs.some((l) => l.exit_at == null) && (
          <button
            type="button"
            className="btn btn-secondary px-3 py-1 text-xs"
            title="Book every open leg at this minute's price"
            onClick={onCloseAll}
          >
            Close all
          </button>
        )}
        <button type="button" className="btn btn-secondary px-3 py-1 text-xs" onClick={onClear}>
          Clear basket
        </button>
      </div>
    </div>
  );
}
